// TripContext — expose the currently-active trip data + favorites.
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "../auth/AuthContext";
import { TRIP as STATIC_TRIP } from "../data/trip";

const TripContext = createContext({ trip: STATIC_TRIP, loading: false });

// Common city → flag emoji mapping (extensible for new trips).
const CITY_FLAGS = {
  "Madrid": "🇪🇸", "Barcelona": "🇪🇸", "Sevilla": "🇪🇸", "Málaga": "🇪🇸",
  "Granada": "🇪🇸", "Ronda": "🇪🇸", "Cádiz": "🇪🇸", "Setenil": "🇪🇸",
  "Setenil de las Bodegas": "🇪🇸", "Vejer": "🇪🇸", "Vejer de la Frontera": "🇪🇸",
  "Grazalema": "🇪🇸", "Zahara": "🇪🇸", "Zahara de la Sierra": "🇪🇸",
  "Praga": "🇨🇿", "Prague": "🇨🇿",
  "Budapest": "🇭🇺", "Buda": "🇭🇺", "Pest": "🇭🇺",
  "Tel Aviv": "🇮🇱", "Jerusalén": "🇮🇱", "Jerusalem": "🇮🇱", "Eilat": "🇮🇱",
  "Masada": "🇮🇱", "Haifa": "🇮🇱",
  "París": "🇫🇷", "Paris": "🇫🇷",
  "Londres": "🇬🇧", "London": "🇬🇧",
  "Roma": "🇮🇹", "Florencia": "🇮🇹", "Venecia": "🇮🇹", "Milán": "🇮🇹",
  "Lisboa": "🇵🇹", "Oporto": "🇵🇹",
  "Nueva York": "🇺🇸", "New York": "🇺🇸",
  "Buenos Aires": "🇦🇷",
  "Santiago": "🇨🇱",
};

// Normalize wizard-produced trip shape (places/restaurants as arrays, stops as
// objects) into the legacy shape expected by Home/DayDetail/PlaceDetail/etc.
// (places as id→doc map, restaurants as city→[]map, stops as "kind:id" strings).
// Also synthesizes a `meta` object so Home.jsx (which reads trip.meta.*) works
// for wizard-created trips that only have top-level title/subtitle/dates.
function normalizeTripData(data, meta) {
  if (!data) return data;
  let out = { ...data };

  if (Array.isArray(out.places)) {
    const map = {};
    for (const p of out.places) {
      if (p && p.id) map[p.id] = p;
    }
    out.places = map;
  }

  if (Array.isArray(out.restaurants)) {
    const map = {};
    for (const r of out.restaurants) {
      if (!r) continue;
      const city = r.city || r.area || "Otros";
      if (!map[city]) map[city] = [];
      map[city].push(r);
    }
    out.restaurants = map;
  }

  if (Array.isArray(out.days)) {
    out.days = out.days.map((d, idx) => {
      if (!d) return d;
      const rawStops = d.stops || [];
      let stops;
      if (rawStops.length > 0 && typeof rawStops[0] === "string") {
        stops = rawStops;
      } else {
        stops = [];
        for (const s of rawStops) {
          if (typeof s === "string") stops.push(s);
          else if (s && s.id) stops.push(`place:${s.id}`);
        }
        for (const rid of (d.restaurant_ids || [])) {
          stops.push(`restaurant:${rid}`);
        }
      }
      return {
        ...d,
        id: d.id || `day-${d.n || idx + 1}`,
        stops,
        // Legacy fields some pages read directly:
        flag: d.flag || CITY_FLAGS[d.city] || "📍",
        dayLabel: d.dayLabel || d.city || "",
        summary: d.summary || d.narrative || "",
      };
    });
  }

  // Synthesize meta if missing (wizard trips keep title/dates at top level only).
  if (!out.meta && meta) {
    const travelersArr = out.travelers || [];
    const travelersStr = travelersArr.length
      ? travelersArr.map((t) => t?.name || t).filter(Boolean).join(" & ")
      : "Alan & Carolina";
    out.meta = {
      title: meta.title || "Mi viaje",
      subtitle: meta.subtitle || "",
      travelers: travelersStr,
      dates: meta.start_date && meta.end_date ? `${meta.start_date} — ${meta.end_date}` : "",
      startDate: meta.start_date,
      endDate: meta.end_date,
      cities: meta.cities || [],
    };
  }

  // Ensure required collections exist
  if (!out.days) out.days = [];
  if (!out.places) out.places = {};
  if (!out.restaurants) out.restaurants = {};
  if (!out.flights) out.flights = [];
  if (!out.hotels) out.hotels = [];

  return out;
}

export function TripProvider({ children }) {
  const params = useParams();
  const tripId = params.tripId;
  const [trip, setTrip] = useState(null);
  const [tripMeta, setTripMeta] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTrip = useCallback(async () => {
    if (!tripId) {
      // Legacy single-trip mode: fall back to the static Europe trip.
      setTrip(STATIC_TRIP);
      return;
    }
    setLoading(true);
    try {
      const t = await api(`/api/trips/${tripId}`);
      const metaForNorm = {
        title: t.title,
        subtitle: t.subtitle,
        start_date: t.start_date,
        end_date: t.end_date,
        cities: t.cities,
      };
      setTrip(normalizeTripData(t.data, metaForNorm) || null);
      setTripMeta({
        trip_id: t.trip_id,
        title: t.title,
        subtitle: t.subtitle,
        start_date: t.start_date,
        end_date: t.end_date,
        cities: t.cities,
        hero_image: t.hero_image,
        share_token: t.share_token,
      });
      try {
        const favs = await api(`/api/trips/${tripId}/favorites`);
        setFavorites(favs);
      } catch {
        setFavorites([]);
      }
    } catch {
      // Fetch failed — keep trip empty so no legacy data leaks in. Pages must
      // handle null trip gracefully.
      setTrip(null);
      setTripMeta(null);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    loadTrip();
  }, [loadTrip]);

  // Recargar el viaje cuando la app vuelva al primer plano (cambio de pestaña,
  // app standalone iOS reanudada, etc.) para reflejar cambios hechos desde otro
  // dispositivo o sesión sin obligar al usuario a reabrir manualmente.
  useEffect(() => {
    if (!tripId) return;
    const onFocus = () => {
      if (document.visibilityState === "visible") loadTrip();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [tripId, loadTrip]);

  const toggleFavorite = async (kind, ref) => {
    const existing = favorites.find((f) => f.kind === kind && f.ref === ref);
    if (existing) {
      await api(`/api/trips/${tripId}/favorites?kind=${encodeURIComponent(kind)}&ref=${encodeURIComponent(ref)}`, { method: "DELETE" });
      setFavorites(favorites.filter((f) => !(f.kind === kind && f.ref === ref)));
    } else {
      const fav = await api(`/api/trips/${tripId}/favorites`, {
        method: "POST",
        body: JSON.stringify({ kind, ref }),
      });
      setFavorites([...favorites, fav]);
    }
  };

  const isFavorite = (kind, ref) => favorites.some((f) => f.kind === kind && f.ref === ref);

  const saveTripData = async (newData) => {
    if (!tripId) return;
    setTrip(newData);
    await api(`/api/trips/${tripId}`, {
      method: "PATCH",
      body: JSON.stringify({ data: newData }),
    });
  };

  return (
    <TripContext.Provider value={{ trip, tripMeta, tripId, favorites, loading, toggleFavorite, isFavorite, saveTripData, reload: loadTrip }}>
      {children}
    </TripContext.Provider>
  );
}

export function useTrip() {
  return useContext(TripContext);
}
